<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InboxCard from 'dashboard/components-next/captain/assistant/InboxCard.vue';
import ConnectInboxDialog from 'dashboard/components-next/captain/pageComponents/inbox/ConnectInboxDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import AssistantScheduleSettingsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantScheduleSettingsForm.vue';
import DicaDoCampo from 'dashboard/components-next/captain/pageComponents/assistant/DicaDoCampo.vue';

// Aba "Onde atua" (fatia 2 das 5 abas, 02/09/2026): em quais caixas a
// assistente trabalha, como (modo por caixa, em linguagem humana) e quando
// (horário). Reaproveita o cartão de caixa, a conexão e a agenda que já existiam;
// o que muda é o lugar e a explicação.
const props = defineProps({
  assistantId: { type: Number, required: true },
  assistant: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['submit']);

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();

const caixas = useMapGetter('captainInboxes/getRecords');
const uiFlags = useMapGetter('captainInboxes/getUIFlags');
const carregando = computed(() => uiFlags.value.fetchingList);

const dialogType = ref('');
const connectInboxDialog = ref(null);
const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const conectar = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value?.dialogRef?.open());
};
const fecharConexao = () => {
  dialogType.value = '';
};
const acaoDaCaixa = ({ action, id }) => {
  if (action !== 'delete') return;
  selectedInbox.value = caixas.value.find(inbox => inbox.id === id);
  nextTick(() => disconnectInboxDialog.value?.dialogRef?.open());
};

watch(
  () => props.assistantId,
  id => {
    if (id) store.dispatch('captainInboxes/get', { assistantId: id });
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-8" data-testid="aba-onde-atua">
    <section class="flex flex-col gap-4">
      <header class="flex flex-col items-start gap-2">
        <h2
          class="flex items-center gap-2 text-base font-medium text-n-slate-12"
        >
          {{ t('CAPTAIN.ONDE_ATUA.CAIXAS.TITULO') }}
          <DicaDoCampo termo="MODO" />
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ONDE_ATUA.CAIXAS.DESCRICAO') }}
        </p>
      </header>

      <div v-if="carregando" class="flex justify-center py-6">
        <Spinner />
      </div>
      <div
        v-else-if="!caixas.length"
        class="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-n-weak p-5"
        data-testid="onde-atua-vazio"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ONDE_ATUA.CAIXAS.VAZIO_TITULO') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ONDE_ATUA.CAIXAS.VAZIO_TEXTO') }}
        </span>
        <Button
          v-if="isAdmin"
          :label="t('CAPTAIN.ONDE_ATUA.CAIXAS.CONECTAR')"
          icon="i-ri-add-line"
          class="!w-fit"
          data-testid="onde-atua-conectar-vazio"
          @click="conectar"
        />
      </div>
      <div v-else class="flex flex-col gap-4">
        <InboxCard
          v-for="caixa in caixas"
          :id="caixa.id"
          :key="caixa.id"
          :inbox="caixa"
          @action="acaoDaCaixa"
        />
        <Button
          v-if="isAdmin"
          :label="t('CAPTAIN.ONDE_ATUA.CAIXAS.CONECTAR')"
          icon="i-ri-add-line"
          color="slate"
          class="!w-fit"
          data-testid="onde-atua-conectar"
          @click="conectar"
        />
      </div>
    </section>

    <span class="h-px w-full bg-n-weak" />

    <section class="flex flex-col gap-4">
      <header class="flex flex-col items-start gap-2">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('CAPTAIN.ONDE_ATUA.HORARIO.TITULO') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ONDE_ATUA.HORARIO.DESCRICAO') }}
        </p>
      </header>
      <AssistantScheduleSettingsForm
        :assistant="assistant"
        @submit="emit('submit', $event)"
      />
    </section>

    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{ assistantId, inboxId: selectedInbox.id }"
      type="Inboxes"
    />
    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="fecharConexao"
    />
  </div>
</template>
